import CSV from 'comma-separated-values';
import { concat, append, isNil, insert, reverse, isEmpty } from 'ramda';
import CSVExportService from 'mangools-commons/dist/services/CSVExportService';

import KeywordSourceTypes from 'constants/KeywordSourceTypes';
import searchVolumeTimeframeTypes from 'constants/SearchVolumeTimeframeTypes';
import { getRelativeKD } from '../utils/relativeKD';
import { SERP_FEATURE_MAP } from 'mangools-react-components/src/constants/serpFeatures';

class ExportService {
    static exportKeywords(keywords, { kwSourceType, currency, urlDataLps, relativeKdActive, ownTarget }) {
        const isCompetitorSourceType = kwSourceType === KeywordSourceTypes.COMPETITOR;
        const isKeywordGapSourceType = kwSourceType === KeywordSourceTypes.KEYWORD_GAP;

        let additionalColumns = [];
        let competitors = [];

        if (isKeywordGapSourceType) {
            if (keywords.length > 0) {
                const competitorsData = keywords[0].competitorData ?? {};
                const ownTargetLabel = ownTarget && String(ownTarget).trim().length > 0
                    ? ownTarget
                    : 'Your domain';
                additionalColumns.push(`SERP Position - ${ownTargetLabel}`);
                additionalColumns.push(`Estimated Traffic - ${ownTargetLabel}`);
                Object.keys(competitorsData).map(key => {
                    competitors.push(key);
                    additionalColumns.push(`SERP Position - ${key}`);
                    additionalColumns.push(`Estimated Traffic - ${key}`);
                })
            }
        }

        const isWithSearchVolume = true; // Always include search volume

        let columns = [
            'Keyword',
            ...(isWithSearchVolume ?
                [
                    isCompetitorSourceType ? 'Avg. Search Volume (Last 12 months)' : 'Avg. Search Volume (Last Known Values)',
                ]
                : []
            ),
            'Location',
            `CPC/${currency.code}`,
            'PPC',
            'Keyword Difficulty',
            'Keyword interest growth (%)',
            'Content Type',
            'Search Intent',
            'Keyword Opportunities',
            'Serp Features',
            ...additionalColumns
        ];

        let data = [];
        let msvDates = null;
        let searchVolumeColumns = null;
        const startEndMsv = ExportService.getStartEndMsvDate(keywords);

        if (!isNil(startEndMsv) && !isKeywordGapSourceType) {
            msvDates = ExportService.getMsvDates(startEndMsv.start, startEndMsv.end);
            searchVolumeColumns = ExportService.generateSearchVolumeColumns(msvDates);
            columns = concat(columns, searchVolumeColumns);
        }

        data = keywords.map(keyword => {
            let keywordData = [
                keyword.keyword,
                ...(isWithSearchVolume ? [
                    CSVExportService.formatEmptyValue(
                        keyword.searchVolumesByTimeframe[
                        isCompetitorSourceType
                            ? searchVolumeTimeframeTypes.LAST_12_MONTHS
                            : searchVolumeTimeframeTypes.LAST_KNOWN_VALUES
                        ],
                    )
                ] : []),
                keyword.location.label,
                ExportService.formatCpc(keyword.cpc, currency),
                CSVExportService.formatEmptyValue(keyword.ppc),
                CSVExportService.formatEmptyValue(
                    relativeKdActive ? getRelativeKD(keyword.rank, urlDataLps) : keyword.rank,
                ),
                CSVExportService.formatEmptyValue(keyword.msvTrends),
                CSVExportService.formatEmptyValue((keyword.contentTypes ?? []).join(", ")),
                CSVExportService.formatEmptyValue((keyword.searchIntent ?? []).join(", ")),
                CSVExportService.formatEmptyValue(([
                    ...(keyword?.kwOpps?.missingWordsInTitle?.length > 0 ? ['Missing words in title'] : []),
                    ...(keyword?.kwOpps?.isOutdatePage ? ['Outdated page'] : []),
                ] ?? []).join(", ")),
                CSVExportService.formatEmptyValue((keyword?.serpFeatures ?? []).filter(feature => SERP_FEATURE_MAP[feature]).map((feature) => SERP_FEATURE_MAP[feature].filterItemLabel).join(", ")),
            ];


            if (!isNil(startEndMsv) && !isKeywordGapSourceType) {
                const searchVolumeData = ExportService.setSearchVolumeData(
                    keyword.monthlySearchVolumes,
                    msvDates,
                    keyword.keyword,
                );

                keywordData = concat(keywordData, searchVolumeData);
            }

            if (isCompetitorSourceType || isKeywordGapSourceType) {
                if (isCompetitorSourceType) {
                    keywordData = insert(1, keyword.serpPosition, keywordData);
                    keywordData = insert(3, keyword.estimatedTraffic, keywordData);
                } else if (isKeywordGapSourceType) {
                    if (isKeywordGapSourceType) {
                        keywordData.push(
                            CSVExportService.formatEmptyValue("-"),
                            CSVExportService.formatEmptyValue("-"),
                        );
                        if (competitors.length > 0) {
                            competitors.forEach(competitor => {
                                keywordData.push(
                                    CSVExportService.formatEmptyValue(keyword[`competitor-${competitor}-rank`] || "-"),
                                    CSVExportService.formatEmptyValue(keyword[`competitor-${competitor}-visits`] || "-"),
                                );
                            });
                        }
                    }
                }
            } else {
                const last12Months = CSVExportService.formatEmptyValue(
                    keyword.searchVolumesByTimeframe[searchVolumeTimeframeTypes.LAST_12_MONTHS],
                );
                const last6Months = CSVExportService.formatEmptyValue(
                    keyword.searchVolumesByTimeframe[searchVolumeTimeframeTypes.LAST_6_MONTHS],
                );
                const last3Months = CSVExportService.formatEmptyValue(
                    keyword.searchVolumesByTimeframe[searchVolumeTimeframeTypes.LAST_3_MONTHS],
                );

                keywordData = insert(2, last12Months, keywordData);
                keywordData = insert(3, last6Months, keywordData);
                keywordData = insert(4, last3Months, keywordData);
            }

            return keywordData;
        });

        if (isKeywordGapSourceType) {
            // its ok
        } else if (isCompetitorSourceType) {
            columns = insert(1, 'SERP Position', columns);
            columns = insert(3, 'Estimated Visits', columns);
        } else {
            columns = insert(2, 'Avg. Search Volume (Last 12 months)', columns);
            columns = insert(3, 'Avg. Search Volume (Last 6 months)', columns);
            columns = insert(4, 'Avg. Search Volume (Last 3 months)', columns);
        }

        return CSV.encode(data, { header: columns });
    }

    static generateSearchVolumeColumns(dates) {
        return dates.map(({ month, year }) => `Search Volume ${month + 1}/${year}`);
    }

    static setSearchVolumeData(monthlySearchVolumes, dates) {
        const msvCountMap = ExportService.getMsvCountMap(monthlySearchVolumes);

        return dates.map(({ month, year }) => {
            const count = msvCountMap[ExportService.generateMsvMapKey(month, year)];

            return CSVExportService.formatEmptyValue(count);
        });
    }

    static getMsvCountMap(monthlySearchVolumes) {
        return monthlySearchVolumes.reduce((map, { count, month, year }) => {
            // eslint-disable-next-line no-param-reassign
            map[ExportService.generateMsvMapKey(month, year)] = count;

            return map;
        }, {});
    }

    static generateMsvMapKey(month, year) {
        return `${month}-${year}`;
    }

    static getMsvDates(start, end) {
        let dates = [];

        // eslint-disable-next-line prefer-destructuring, no-plusplus
        for (let year = start.year; year <= end.year; year++) {
            let startMonth = 0;
            let endMonth = 12;

            if (year === start.year) {
                startMonth = start.month;
            }

            if (year === end.year) {
                endMonth = end.month + 1;
            }

            // eslint-disable-next-line no-plusplus
            for (startMonth; startMonth < endMonth; startMonth++) {
                dates = append({ month: startMonth, year }, dates);
            }
        }

        return dates;
    }

    static getStartEndMsvDate(keywords) {
        const firstKwWithMsv = ExportService.getFirstKwWithMsv(keywords);
        const lastKwWithMsv = ExportService.getLastKwWithMsv(keywords);

        if (isNil(firstKwWithMsv) || isNil(lastKwWithMsv)) {
            return null;
        }

        let start = {
            month: firstKwWithMsv.monthlySearchVolumes[0].month,
            year: firstKwWithMsv.monthlySearchVolumes[0].year,
        };

        let end = {
            month: lastKwWithMsv.monthlySearchVolumes[0].month,
            year: lastKwWithMsv.monthlySearchVolumes[0].year,
        };

        keywords.forEach(({ monthlySearchVolumes }) => {
            monthlySearchVolumes.forEach(({ month, year }) => {
                if (year < start.year) {
                    start = { month, year };
                } else if (year === start.year) {
                    if (month < start.month) {
                        start = { month, year };
                    }
                }

                if (year > end.year) {
                    end = { month, year };
                } else if (year === end.year) {
                    if (month > end.month) {
                        end = { month, year };
                    }
                }
            });
        });

        return { start, end };
    }

    static getFirstKwWithMsv(keywords) {
        return keywords.find(({ monthlySearchVolumes }) => monthlySearchVolumes.length > 0);
    }

    static getLastKwWithMsv(keywords) {
        return reverse(keywords).find(({ monthlySearchVolumes }) => monthlySearchVolumes.length > 0);
    }

    static formatCpc(value, currency) {
        const formattedValue = CSVExportService.formatEmptyValue(value);
        if (isEmpty(formattedValue)) {
            return formattedValue;
        } else {
            return (value * currency.rate).toFixed(2);
        }
    }
}

export default ExportService;
